import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Flex,
  Heading,
  Text,
  TextField,
} from "@radix-ui/themes";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { listUsers } from "../api/admin";
import { date, num } from "../lib/format";

export default function Users() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  // Debounce the search box.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const q = useInfiniteQuery({
    queryKey: ["users", debounced],
    queryFn: ({ pageParam }) =>
      listUsers({ search: debounced, cursor: pageParam, limit: 50 }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const users = q.data?.pages.flatMap((p) => p.users) ?? [];
  const total = q.data?.pages[0]?.total ?? 0;

  return (
    <Flex direction="column" gap="3">
      <Flex justify="between" align="center" wrap="wrap" gap="2">
        <Heading size="5">Users</Heading>
        {q.data && (
          <Text size="1" className="hq-muted">
            {num(total)} total
          </Text>
        )}
      </Flex>

      <TextField.Root
        placeholder="Search by email or name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="2"
      >
        <TextField.Slot>
          <MagnifyingGlassIcon />
        </TextField.Slot>
      </TextField.Root>

      {q.isError && (
        <Text size="2" color="red">
          {String(q.error as Error)}
        </Text>
      )}

      <table className="hq-table">
        <thead>
          <tr>
            <th>Email</th>
            <th>Name</th>
            <th>Status</th>
            <th>Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr
              key={u.id}
              className="is-clickable"
              onClick={() => navigate(`/users/${encodeURIComponent(u.id)}`)}
            >
              <td>{u.email}</td>
              <td>{u.name ?? <span className="hq-muted">—</span>}</td>
              <td>
                {u.emailVerified ? (
                  <Badge color="green">verified</Badge>
                ) : (
                  <Badge color="amber">unverified</Badge>
                )}
              </td>
              <td>{date(u.createdAt)}</td>
            </tr>
          ))}
          {users.length === 0 && !q.isLoading && (
            <tr>
              <td colSpan={4}>
                <Text size="2" className="hq-muted">
                  No users.
                </Text>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {q.hasNextPage && (
        <Flex justify="center">
          <Button
            variant="soft"
            color="gray"
            onClick={() => q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
          >
            {q.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </Flex>
      )}
    </Flex>
  );
}
